import { Button, StatusBadge } from "@fawkes/ui";
import { AlertTriangle, CalendarCheck, CalendarDays, Send } from "lucide-react";

import { formatDate, formatEuro } from "../../i18n/format";
import type { ReserveringAanvraag } from "./types";

type T = (key: string, options?: { args?: unknown[] }) => string;

/**
 * Een reserveringsaanvraag uit de wizard van het portaal (RESERVERING_NIEUW), zoals hij nu in de backoffice zou
 * landen: de namen horen bij de id's zoals ze nu zijn, met de prijscontrole (wat de klant zag tegenover wat het nu
 * kost) en de beschikbaarheid. Ligt de prijs boven de drempel, dan kan goedkeuren niet en doet de medewerker een
 * tegenvoorstel met de nieuwe prijs (ontwerp 12, "Prijs bij aanvraag versus prijs bij bevestiging").
 */
export function ReserveringBlok({ t, locale, r, kanVoorstellen, onVoorstel }: {
  t: T;
  locale: string;
  r: ReserveringAanvraag;
  kanVoorstellen: boolean;
  onVoorstel: (prijs: number | null) => void;
}) {
  const p = r.prijs;
  return (
    <>
      <table className="kvd__tabel">
        <tbody>
          <tr>
            <th>{t("klantverzoeken.reservering.periode")}</th>
            <td>
              {formatDate(r.aankomst, locale)} {r.aankomstTijd ?? ""} – {formatDate(r.vertrek, locale)} {r.vertrekTijd ?? ""}
              {r.vestiging && <span className="kvd__stil"> · {r.vestiging}</span>}
            </td>
          </tr>
          {r.dieren.map((d) => (
            <tr key={d.dierId}>
              <th>{d.naam}{d.soort && <span className="kvd__stil"> ({d.soort})</span>}</th>
              <td>
                {[d.accommodatie ?? t("klantverzoeken.reservering.geenVoorkeur"),
                  t(`klantverzoeken.reservering.voeding.${d.voeding ?? "STANDAARD"}`),
                  d.medicatie ? t("klantverzoeken.reservering.medicatie") : null,
                  d.samenMetGezin ? t("klantverzoeken.reservering.samen") : null,
                  ...d.extras.map((e) => (e.aantal > 1 ? `${e.naam} × ${e.aantal}` : e.naam))]
                  .filter(Boolean).join(" · ")}
                {d.voedingInstructies && <div className="kvd__stil">{d.voedingInstructies}</div>}
                {d.medicatie && d.medicatieInstructies && <div className="kvd__stil">{d.medicatieInstructies}</div>}
                {d.bijzonderheden && <div className="kvd__stil">{d.bijzonderheden}</div>}
              </td>
            </tr>
          ))}
          {r.extras.length > 0 && (
            <tr>
              <th>{t("klantverzoeken.reservering.extras")}</th>
              <td>{r.extras.map((e) => (e.aantal > 1 ? `${e.naam} × ${e.aantal}` : e.naam)).join(" · ")}</td>
            </tr>
          )}
          <tr>
            <th>{t("klantverzoeken.reservering.verzekering")}</th>
            <td>{r.annuleringsverzekering ? t("klantverzoeken.reservering.ja") : t("klantverzoeken.reservering.nee")}</td>
          </tr>
          {r.overigeWensen && (
            <tr>
              <th>{t("klantverzoeken.reservering.wensen")}</th>
              <td>{r.overigeWensen}</td>
            </tr>
          )}
          {r.antwoorden.map((a, i) => (
            <tr key={`a${i}`}>
              <th>{a.vraag}{a.dier && <span className="kvd__stil"> ({a.dier})</span>}</th>
              <td>{a.waarde === "true" ? t("klantverzoeken.reservering.ja") : a.waarde === "false" ? t("klantverzoeken.reservering.nee") : a.waarde}</td>
            </tr>
          ))}
          <tr>
            <th>{t("klantverzoeken.reservering.wizardVersie")}</th>
            <td>{r.wizardVersie === 0 ? t("klantverzoeken.reservering.standaard") : r.wizardVersie}</td>
          </tr>
        </tbody>
      </table>

      <div className={`kvd__prijs${p?.akkoordNodig ? " kvd__prijs--hoger" : ""}`} data-testid="prijscontrole">
        <h3>{t("klantverzoeken.reservering.prijscontrole")}</h3>
        {p?.fout ? (
          <p className="kvd__melding kvd__melding--fout"><AlertTriangle size={16} aria-hidden="true" /> {p.fout}</p>
        ) : p ? (
          <>
            <table className="kvd__tabel">
              <tbody>
                <tr>
                  <th>{t(p.bron === "VOORSTEL" ? "klantverzoeken.reservering.gezienVoorstel" : "klantverzoeken.reservering.gezien")}</th>
                  <td>{p.gezien == null ? "—" : formatEuro(p.gezien, locale)}</td>
                </tr>
                <tr>
                  <th>{t("klantverzoeken.reservering.nu")}</th>
                  <td>
                    <strong>{p.nu == null ? "—" : formatEuro(p.nu, locale)}</strong>
                    {p.verschil != null && p.verschil !== 0 && (
                      <span className="kvd__stil"> ({p.verschil > 0 ? "+" : ""}{formatEuro(p.verschil, locale)})</span>
                    )}
                  </td>
                </tr>
                {p.opbouw?.regels.map((regel, i) => (
                  <tr key={i}>
                    <td className="kvd__stil">{regel.omschrijving}</td>
                    <td className="kvd__stil">{regel.berekening ? `${regel.berekening} = ` : ""}{formatEuro(regel.bedrag, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {p.akkoordNodig ? (
              <div className="kvd__melding" role="status">
                <AlertTriangle size={16} aria-hidden="true" />
                <div>
                  <strong>{t("klantverzoeken.reservering.akkoordNodig")}</strong>
                  <p>{t("klantverzoeken.reservering.akkoordUitleg", { args: [formatEuro(p.toegestaan ?? 0, locale)] })}</p>
                  {kanVoorstellen && (
                    <Button variant="secondary" onClick={() => onVoorstel(p.nu)}>
                      <Send size={16} aria-hidden="true" /> {t("klantverzoeken.reservering.voorstelMetPrijs")}
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <p className="kvd__stil">{t("klantverzoeken.reservering.binnenDrempel")}</p>
            )}
          </>
        ) : null}
      </div>

      {r.beschikbaarheid && (
        <div className="kvd__prijs">
          <h3>{t("klantverzoeken.reservering.beschikbaarheid")}</h3>
          {r.beschikbaarheid.beschikbaar ? (
            <StatusBadge tone={r.beschikbaarheid.capaciteitIngesteld ? "success" : "neutral"}>
              <CalendarCheck size={14} aria-hidden="true" />{" "}
              {r.beschikbaarheid.capaciteitIngesteld ? t("klantverzoeken.reservering.plek") : t("klantverzoeken.reservering.geenCapaciteit")}
            </StatusBadge>
          ) : (
            <ul className="kvd__knelpunten">
              {r.beschikbaarheid.knelpunten.map((k, i) => (
                <li key={i}>
                  <CalendarDays size={14} aria-hidden="true" /> {formatDate(k.datum, locale)} · {k.diersoort}:{" "}
                  {k.reden === "VOL" ? t("klantverzoeken.reservering.vol") : t("klantverzoeken.reservering.vrij", { args: [k.vrij ?? 0] })}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}
